import { Component, NgZone, OnDestroy, OnInit, inject } from '@angular/core';
import { Router, RouterOutlet, RouteReuseStrategy } from '@angular/router';
import { Location } from '@angular/common';
import { App as CapacitorApp } from '@capacitor/app';
import type { PluginListenerHandle } from '@capacitor/core';
import { CustomReuseStrategy } from './custom-reuse-strategy';
import { PlatformService } from './service/platform/platform.service';
import { MobileTabBarComponent } from './mobile-tab-bar/mobile-tab-bar.component';
import { SidebarDrawerComponent } from './sidebar-drawer/sidebar-drawer.component';
import { SkyTransitionComponent } from './sky-coach/sky-transition.component';
import { SkyCoachContext, SkyCoachStateService } from './service/sky/sky-coach-state.service';
import { BrandService } from './service/brand/brand.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, MobileTabBarComponent, SidebarDrawerComponent, SkyTransitionComponent],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  providers: [
    { provide: RouteReuseStrategy, useClass: CustomReuseStrategy },
  ]
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'question-trainer-app';
  protected showLaunchTransition = false;
  protected readonly launchSkyContext: SkyCoachContext = { surface: 'launch' };
  protected readonly brand = inject(BrandService);

  private readonly platform = inject(PlatformService);
  private readonly router = inject(Router);
  private readonly ngLocation = inject(Location);
  private readonly ngZone = inject(NgZone);
  private readonly skyCoachState = inject(SkyCoachStateService);
  private backListener?: Promise<PluginListenerHandle>;

  constructor() {
    if (this.platform.isMobile() && this.brand.showMascot) {
      this.showLaunchTransition = true;
      this.skyCoachState.beginLaunchTransition();
    }
  }

  ngOnInit(): void {
    this.platform.applyBodyClasses();
    if (this.platform.isMobile()) {
      this.wireSystemBack();
    }
  }

  ngOnDestroy(): void {
    void this.backListener?.then(listener => listener.remove());
    this.skyCoachState.endLaunchTransition();
  }

  protected closeLaunchTransition(): void {
    this.showLaunchTransition = false;
    this.skyCoachState.endLaunchTransition();
  }

  /**
   * Route Android's hardware/gesture back through the same handler the in-app
   * back button uses. CanDeactivate guards (test runner) still fire because
   * Location.back() goes through the Angular router; on the home stack with
   * no history left, exit the app to match Android user expectations.
   */
  private wireSystemBack(): void {
    this.backListener = CapacitorApp.addListener('backButton', () => {
      this.ngZone.run(() => {
        if (this.router.url.split(/[?#]/, 1)[0] === '/dashboard') {
          void CapacitorApp.exitApp();
          return;
        }
        if (typeof window !== 'undefined' && window.history.length > 1) {
          this.ngLocation.back();
          return;
        }
        void CapacitorApp.exitApp();
      });
    });
  }

}

